/**
 * Recurring reminders ("việc định kỳ").
 *
 * Two schedules are supported, both stored on the reminder itself:
 *
 *  - `repeatEveryDays` — a rolling chore interval counted from the moment you
 *    tick it off ("Mỗi 7 ngày", the Wheneri model). Completing moves the due
 *    date forward; there is never a "done" state for the series.
 *  - `rrule` — a fixed schedule (every Monday, the 1st of the month…).
 *    Completing marks the occurrences that came due as done, and the task
 *    rolls on to the next one.
 *
 * In both cases the list shows a single "current" occurrence: the newest one
 * that is due (overdue counts) or, when everything is done, the next one.
 */

import type { Reminder } from "@lockcal/shared";
import { endOfDay, MS_DAY, startOfDay, WEEKDAY_LABELS_ISO } from "./date";
import { expandOccurrences, parseRRule } from "./recurrence";

export interface ReminderOccurrence {
  /** Due timestamp of this occurrence. */
  start: number;
  /** Due before the start of today. */
  overdue: boolean;
  /** Due today. */
  today: boolean;
  /** Still ahead (nothing is due yet). */
  upcoming: boolean;
  /** Part of a recurring series. */
  recurring: boolean;
}

/**
 * Everything the recurrence helpers need. Kept structural so both the full
 * sync record and the lighter `ReminderItem` from `useReminders` fit.
 */
export type ReminderLike = Pick<
  Reminder,
  | "dueEpochMillis"
  | "isCompleted"
  | "completedAtEpochMillis"
  | "rrule"
  | "repeatEveryDays"
  | "completedOccurrenceStarts"
  | "completionHistory"
>;

export type ReminderPatch = Partial<
  Pick<
    Reminder,
    | "dueEpochMillis"
    | "isCompleted"
    | "completedAtEpochMillis"
    | "rrule"
    | "repeatEveryDays"
    | "completedOccurrenceStarts"
    | "completionHistory"
  >
>;

export function isRecurring(
  reminder: Pick<Reminder, "rrule" | "repeatEveryDays">,
): boolean {
  return (reminder.repeatEveryDays ?? 0) > 0 || Boolean(reminder.rrule);
}

function decorate(
  start: number,
  now: number,
  recurring: boolean,
): ReminderOccurrence {
  const dayStart = startOfDay(now).getTime();
  const dayEnd = endOfDay(now).getTime();
  return {
    start,
    overdue: start < dayStart,
    today: start >= dayStart && start <= dayEnd,
    upcoming: start > dayEnd,
    recurring,
  };
}

/**
 * All occurrences of a fixed schedule up to (and including) `through`.
 * The look-back window keeps daily chores from being expanded forever.
 */
function occurrencesThrough(reminder: ReminderLike, through: number) {
  if (reminder.dueEpochMillis === null) return [];
  const from = reminder.dueEpochMillis;
  return expandOccurrences({
    rrule: reminder.rrule,
    start: from,
    end: from,
    rangeStart: Math.max(from, through - 730 * MS_DAY),
    rangeEnd: through + 1,
    maxOccurrences: 2000,
  });
}

/** The occurrence that the checkbox in a list acts on. */
export function currentOccurrence(
  reminder: ReminderLike,
  now = Date.now(),
): ReminderOccurrence | null {
  if (!isRecurring(reminder)) {
    if (reminder.dueEpochMillis === null) return null;
    return decorate(reminder.dueEpochMillis, now, false);
  }

  if ((reminder.repeatEveryDays ?? 0) > 0) {
    return reminder.dueEpochMillis === null
      ? null
      : decorate(reminder.dueEpochMillis, now, true);
  }

  // Fixed schedule without a parseable rule: fall back to the due date so the
  // task never disappears because of a bad rule string.
  if (!parseRRule(reminder.rrule) || reminder.dueEpochMillis === null) {
    return reminder.dueEpochMillis === null
      ? null
      : decorate(reminder.dueEpochMillis, now, false);
  }

  const completed = new Set(reminder.completedOccurrenceStarts ?? []);
  const due = occurrencesThrough(reminder, now).filter(
    (occurrence) => !completed.has(occurrence.start),
  );

  const pending = due.filter((occurrence) => occurrence.start <= now).at(-1);
  if (pending) return decorate(pending.start, now, true);

  // Nothing pending: show the next date so the list still carries the task.
  const next = expandOccurrences({
    rrule: reminder.rrule,
    start: reminder.dueEpochMillis,
    end: reminder.dueEpochMillis,
    rangeStart: now + 1,
    rangeEnd: now + 400 * MS_DAY,
    maxOccurrences: 1,
  }).find((occurrence) => !completed.has(occurrence.start));

  return next ? decorate(next.start, now, true) : null;
}

/**
 * Apply a completion to a reminder.
 *
 * Returns the fields to write plus a snapshot that restores the previous
 * state (used by the Undo action).
 */
export function completeReminder(
  reminder: ReminderLike,
  now = Date.now(),
): { patch: ReminderPatch; undo: ReminderPatch } | null {
  const undo: ReminderPatch = {
    dueEpochMillis: reminder.dueEpochMillis,
    isCompleted: reminder.isCompleted,
    completedAtEpochMillis: reminder.completedAtEpochMillis,
    rrule: reminder.rrule,
    repeatEveryDays: reminder.repeatEveryDays ?? null,
    completedOccurrenceStarts: reminder.completedOccurrenceStarts ?? [],
    completionHistory: reminder.completionHistory ?? [],
  };

  if (!isRecurring(reminder)) {
    return {
      patch: { isCompleted: true, completedAtEpochMillis: now },
      undo,
    };
  }

  const occurrence = currentOccurrence(reminder, now);
  if (!occurrence) return null;

  const history = [...(reminder.completionHistory ?? []), now]
    .sort((a, b) => a - b)
    .slice(-500);

  // Rolling chore: the next date counts from the completion, keeping the
  // original time of day so the list does not drift to midnight.
  if ((reminder.repeatEveryDays ?? 0) > 0) {
    const previous = new Date(occurrence.start);
    const next = new Date(now + (reminder.repeatEveryDays ?? 1) * MS_DAY);
    next.setHours(previous.getHours(), previous.getMinutes(), 0, 0);
    const nextDue =
      next.getTime() <= now ? next.getTime() + MS_DAY : next.getTime();

    return {
      patch: { dueEpochMillis: nextDue, completionHistory: history },
      undo,
    };
  }

  // Fixed schedule: tick off every occurrence up to the one shown, so a chore
  // that was missed twice is fully caught up instead of staying overdue.
  const done = new Set(reminder.completedOccurrenceStarts ?? []);
  for (const item of occurrencesThrough(reminder, occurrence.start)) {
    done.add(item.start);
  }

  return {
    patch: {
      completedOccurrenceStarts: [...done].sort((a, b) => a - b).slice(-500),
      completionHistory: history,
    },
    undo,
  };
}

/** Human label for the schedule: "Mỗi 7 ngày", "T2, T5", "Hằng ngày"… */
export function repeatLabel(
  reminder: Pick<Reminder, "rrule" | "repeatEveryDays">,
): string | null {
  const every = reminder.repeatEveryDays ?? 0;
  if (every > 0) return every === 1 ? "Hằng ngày" : `Mỗi ${every} ngày`;

  const parts = parseRRule(reminder.rrule);
  if (!parts) return null;

  if (parts.freq === "DAILY") {
    return parts.interval === 1 ? "Hằng ngày" : `Mỗi ${parts.interval} ngày`;
  }

  if (parts.freq === "WEEKLY") {
    const days = parts.byDay ?? [];
    if (days.length === 0) return "Hằng tuần";
    if (days.length === 7) return "Hằng ngày";
    return [...days]
      .sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))
      .map((day) => WEEKDAY_LABELS_ISO[(day + 6) % 7])
      .join(", ");
  }

  if (parts.freq === "MONTHLY") {
    return parts.byMonthDay
      ? `Ngày ${parts.byMonthDay} hằng tháng`
      : "Hằng tháng";
  }

  return "Hằng năm";
}

/** Most recent completions, newest first. */
export function recentCompletions(
  reminder: Pick<Reminder, "completionHistory">,
  limit = 5,
): number[] {
  return [...(reminder.completionHistory ?? [])].sort((a, b) => b - a).slice(0, limit);
}
